import { useThree, invalidate } from '@react-three/fiber';
import { memo, type MutableRefObject, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Float32BufferAttribute,
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
  Scene,
  SRGBColorSpace,
  Uint32BufferAttribute,
  WebGLRenderTarget,
} from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import { SMAAPass } from 'three/addons/postprocessing/SMAAPass.js';
import isEqual from 'fast-deep-equal';
import { useThrottle } from 'react-use';
import { useSelector } from 'app/store';
import { dispatcher, EditorEventType } from 'modules/editor/events';
import { Z } from 'modules/editor/constants';
import { linksCoverageParamsSelector } from 'modules/links/externalSelectors';
import { useEditorStore } from 'modules/editor/store';
import { CoverageLineSeparatorShader } from 'modules/editor/containers/coverage/CoverageLineSeparatorShader';
import type { CoverageScene } from './types';

const useCoverageWorkerScene = (mesh: MutableRefObject<Mesh<PlaneGeometry, MeshBasicMaterial> | undefined>) => {
  const [, setStamp] = useState(0);

  const gl = useThree((store) => store.gl);
  const size = useThree((store) => store.size);
  const camera = useThree((store) => store.camera);

  const cache = useMemo(() => {
    return {
      item: new MeshBasicMaterial({ vertexColors: true }),
      coverage: new MeshBasicMaterial({ transparent: true, opacity: 0.7 }),
    };
  }, []);

  // Create new scene and render target
  const [scene, composer] = useMemo(() => {
    const scene = new Scene() as CoverageScene;

    const composer = new EffectComposer(
      gl,
      new WebGLRenderTarget(undefined, undefined, { colorSpace: SRGBColorSpace })
    );
    composer.renderToScreen = false;

    const effectLine = new ShaderPass(CoverageLineSeparatorShader);
    effectLine.setSize = (width, height) => {
      (effectLine.uniforms as typeof CoverageLineSeparatorShader.uniforms).resolution.value.set(width, height);
    };

    composer.addPass(new RenderPass(scene, camera, undefined, undefined, 0));
    composer.addPass(effectLine);
    composer.addPass(new SMAAPass());

    scene.hash = {};
    return [scene, composer] as const;
  }, [gl, camera]);

  // Render scenes content into target's texture
  scene.rerender = useCallback(() => {
    composer.render();

    if (mesh.current) {
      const { width, height, texture } = composer.writeBuffer;

      mesh.current.renderOrder = Z.COVERAGE;
      mesh.current.material = cache.coverage;
      mesh.current.material.map = texture;

      if (mesh.current.geometry.parameters?.width !== width || mesh.current.geometry.parameters?.height !== height) {
        mesh.current.geometry = new PlaneGeometry(width, height);
      }
    }

    invalidate();
  }, [composer, mesh, cache]);

  // Rebuild mesh geometry
  scene.rebuild = useCallback(
    (deviceId, [index, color, position]) => {
      const mesh = scene.hash[deviceId];
      if (!mesh) {
        return composer;
      }

      const geometry = mesh.geometry;
      geometry
        .setIndex(new Uint32BufferAttribute(index, 1))
        .setAttribute('color', new Float32BufferAttribute(color, 3))
        .setAttribute('position', new Float32BufferAttribute(position, 3));

      geometry.getIndex()!.needsUpdate = true;
      geometry.getAttribute('color').needsUpdate = true;
      geometry.getAttribute('position').needsUpdate = true;

      geometry.boundingBox = null;
      geometry.boundingSphere = null;

      scene.rerender();

      return composer;
    },
    [scene, composer]
  );

  // Handle mesh creation
  scene.handleCoverageItem = useCallback(
    (deviceId) => {
      const mesh = new Mesh(undefined, cache.item);
      mesh.matrixAutoUpdate = false;
      mesh.matrixWorldNeedsUpdate = false;

      scene.add(mesh);
      scene.hash[deviceId] = mesh;

      return () => {
        scene.send.deviceRemove(deviceId);
        delete scene.hash[deviceId];
        mesh.geometry?.dispose();
        scene.remove(mesh);
        scene.rerender();
      };
    },
    [scene, cache]
  );

  scene.handleViewport = useCallback(() => {
    const [x, y] = camera.position.toArray();
    mesh.current?.scale.setScalar(1 / camera.zoom / composer.renderer.getPixelRatio());
    mesh.current?.position.set(x, y, Z.COVERAGE);
    scene.rerender();
  }, [scene, mesh, camera, composer]);

  // Update target's size
  useEffect(() => {
    composer.setSize(size.width, size.height);
    scene.rerender();
  }, [scene, composer, size.width, size.height]);

  // Remove target
  useEffect(() => () => composer.dispose(), [composer]);

  // Init worker
  useEffect(() => {
    scene.worker = new Worker(new URL('./coverage.worker.ts', import.meta.url), { type: 'module' });
    setStamp(Date.now());

    scene.send = {
      coverageData(coverageData) {
        scene.worker.postMessage({ type: 'coverage-data', data: coverageData });
      },
      devicePosition(deviceId, position) {
        const array = Float64Array.from(position);
        scene.worker.postMessage({ type: 'device-position', data: [deviceId, array.buffer] }, [array.buffer]);
      },
      deviceRemove(deviceId) {
        scene.worker.postMessage({ type: 'device-remove', data: [deviceId] });
      },
    };

    return () => scene.worker.terminate();
  }, [scene]);

  useEffect(() => {
    const listener = () => scene.handleViewport();
    dispatcher.addEventListener(EditorEventType.PAN_CHANGED, listener);
    dispatcher.addEventListener(EditorEventType.ZOOM_CHANGED, listener);

    return () => {
      dispatcher.removeEventListener(EditorEventType.PAN_CHANGED, listener);
      dispatcher.removeEventListener(EditorEventType.ZOOM_CHANGED, listener);
    };
  }, [scene]);

  useEffect(() => scene.handleViewport(), [scene, scene.worker]);

  return scene.worker ? scene : null;
};

const CoverageDeviceListenerImpl = ({ id, scene }: { scene: CoverageScene; id: string }) => {
  useEffect(() => scene.handleCoverageItem(id), [scene, id]);

  const position = useEditorStore((state) => state.devices[id].position);
  const positionThrottled = useThrottle(position, 16);

  useEffect(() => {
    scene.send.devicePosition(id, positionThrottled);
  }, [scene.send, id, positionThrottled]);

  return null;
};

const CoverageDeviceListener = memo(CoverageDeviceListenerImpl);

const CoverageImpl = () => {
  const mesh = useRef<Mesh<PlaneGeometry, MeshBasicMaterial>>(null!);

  const scene = useCoverageWorkerScene(mesh);
  const coverageParams = useSelector(linksCoverageParamsSelector, isEqual);

  useLayoutEffect(() => {
    if (!scene) {
      return;
    }

    scene.send.coverageData(coverageParams);
  }, [scene, coverageParams]);

  useEffect(() => {
    if (!scene) {
      return;
    }

    const listener = (
      message: MessageEvent<{
        type: 'coverage';
        deviceId: string;
        data: [index: ArrayBuffer, color: ArrayBuffer, position: ArrayBuffer];
      }>
    ) => {
      const event = message.data;
      if (event.type === 'coverage') {
        scene.rebuild(event.deviceId, event.data);

        if (import.meta.env.DEV) {
          (window as Window & { __coverageRenderCount?: number }).__coverageRenderCount ??= 0;
          (window as Window & { __coverageRenderCount?: number }).__coverageRenderCount!++;
        }
      }
    };

    scene.worker.addEventListener('message', listener);
    return () => {
      scene.worker.removeEventListener('message', listener);
    };
  }, [scene]);

  if (!scene) {
    return null;
  }

  return (
    <>
      {/* Use target's texture as material for mesh */}
      <mesh ref={mesh} />

      {coverageParams.map(({ apId }) => (
        <CoverageDeviceListener key={apId} scene={scene} id={apId} />
      ))}
    </>
  );
};

export const Coverage = memo(CoverageImpl);
